<script setup lang="ts">
import { ref, onMounted } from 'vue';
import router from '@/router';
import LabelTopLeft from '@/shared/components/ui/LabelTopLeft.vue';
import { IonButton, IonIcon } from '@ionic/vue';
import { location as locationIcon, timeOutline } from 'ionicons/icons';
import { DateTime } from 'luxon';
import { useCacheImage } from '@/shared/composables/useCacheImage';

const props = defineProps({
	id: { type: Number, required: true },
	date: { type: String, required: true },
	dateEnd: { type: String },
	going: { type: Boolean, default: false },
	hour: { type: String, required: true },
	hourEnd: { type: String },
	image: { type: String, required: true },
	link: { type: String, required: true },
	location: { type: String, required: true, default: '' },
	summary: { type: String as () => string | null | undefined, required: true, default: '' },
	ticketCount: { type: Number },
	title: { type: String, required: true },
	showLoggedData: { type: Boolean, required: true, default: true },
	updatedAt: { type: String, default: '' },
});

// Init
const { loadImage } = useCacheImage();
const eventImageUrl = ref();
onMounted(async () => {
	let eventImage = props.image.split('/').pop();
	eventImage = eventImage ?? 'event';
	eventImageUrl.value = await loadImage(`event-${props.id}-main-image`, eventImage, props.image, props.updatedAt);
});
</script>

<template>
	<div class="w-full mb-4 event-item bg-cover bg-center rounded-md backdrop-blur-2xl bg-black/50">
		<div class="relative">
			<div class="event-detail-image aspect-[16/9] relative overflow-hidden">
				<img v-if="eventImageUrl" :src="eventImageUrl" class="w-full h-full relative rounded-lg object-cover object-center" />
				<LabelTopLeft v-if="props.going" class="border-l-anykrowd-item-added-green" label-container-class="left-1 top-2" :label="$t('events.going')" />
			</div>
		</div>
		<div class="flex flex-col p-4">
			<div class="divide-y divide-white/10">
				<div>
					<h1 class="text-l2">{{ props.title }}</h1>
					<div class="pb-2">
						<div v-if="props.date">
							<span class="font-sourcesanspro-regular text-xs2 text-white"> {{ DateTime.fromFormat(props.date, 'yyyy-MM-dd HH:mm:ss').toFormat('dd/MM/yyyy') }} at {{ props.hour }} </span>
							<span v-if="props.dateEnd" class="font-sourcesanspro-regular text-xs2 text-white"> to {{ DateTime.fromFormat(props.dateEnd, 'yyyy-MM-dd HH:mm:ss').toFormat('dd/MM/yyyy') }} at {{ props.hourEnd }} </span>
							<span class="font-sourcesanspro-regular text-xs2 text-white">
								-
								{{ props.location }}
							</span>
						</div>
					</div>
				</div>
				<div class="pt-2 mb-6">
					<p class="font-sourcesanspro-regular text-sm2 text-white/60">
						{{ props.summary }}
					</p>
				</div>
			</div>
			<div class="flex items-center justify-center">
				<router-link :to="props.link" class="w-full">
					<ion-button
						:class="[
							{
								'w-full': !props.going,
								'w-[145px]': props.going,
							},
						]"
						class="h-[48px]"
						color="primary"
						fill="outline"
						shape="round">
						<span class="font-sourcesanspro-semibold text-base2">{{ $t('components.feed_item.view_details') }}</span>
					</ion-button>
				</router-link>

				<router-link v-if="props.showLoggedData && props.going && props.ticketCount" :to="`/events/${id}/tickets`">
					<ion-button class="w-[145px] h-[48px] hover:bg-white hover:rounded-full" shape="round">
						<span class="font-sourcesanspro-semibold text-base2"> {{ props.ticketCount }} {{ $t('events.details.tickets') }} </span>
					</ion-button>
				</router-link>
			</div>
		</div>
	</div>
</template>

<style scoped></style>
